/**
 * Story 7: Emma State Machine - Contextual Help System
 * 
 * Hook for managing Emma's contextual state across workflow steps.
 * Tracks user progress and provides relevant guidance messages.
 * 
 * Usage:
 *   const { emmaState, updateEmmaState, getContextualMessage } = useEmmaState();
 *   updateEmmaState({ state: 'STEP_1_CLIENT_LOADED', context: { clientName: 'Acme Corp' } });
 */

import { useState, useCallback, useEffect } from 'react';

// Emma state machine states
export type EmmaState =
  // Step 1: Client Profile
  | 'STEP_1_CLIENT_EMPTY'
  | 'STEP_1_CLIENT_LOADED'
  // Step 2: Partners
  | 'STEP_2_PARTNERS_EMPTY'
  | 'STEP_2_PARTNERS_LOADED'
  // Step 3: Knowledge Base
  | 'STEP_3_KNOWLEDGE_EMPTY'
  | 'STEP_3_COVERAGE_LOW'
  | 'STEP_3_COVERAGE_MID'
  | 'STEP_3_COVERAGE_HIGH'
  // Step 4: Campaign Setup
  | 'STEP_4_SETUP_REVIEW'
  // Step 5: Generation
  | 'STEP_5_GENERATION_STARTING'
  | 'STEP_5_GENERATION_IN_PROGRESS'
  | 'STEP_5_GENERATION_COMPLETE'
  // Step 6: Content Review
  | 'STEP_6_REVIEW_ASSETS'
  | 'STEP_6_ASSETS_APPROVED'
  // Step 12: Export
  | 'STEP_12_EXPORT';

export interface EmmaStateContext {
  state: EmmaState;
  context?: Record<string, any>;
  timestamp?: Date;
}

export interface EmmaMessage {
  message: string;
  type: 'info' | 'success' | 'warning' | 'tip';
  dismissible: boolean;
  autoDismissMs?: number;
}

// Contextual messages for each state
const STATE_MESSAGES: Record<EmmaState, (context?: Record<string, any>) => EmmaMessage> = {
  STEP_1_CLIENT_EMPTY: () => ({
    message: "Let's start by selecting or creating your client. This helps me understand who we're marketing for.",
    type: 'info',
    dismissible: true,
    autoDismissMs: 8000,
  }),
  
  STEP_1_CLIENT_LOADED: (ctx) => ({
    message: `Great! I see ${ctx?.clientName || 'your client'}. Now let's add partners for this campaign.`,
    type: 'success',
    dismissible: true,
    autoDismissMs: 5000,
  }),
  
  STEP_2_PARTNERS_EMPTY: () => ({
    message: "Tell me, who are the key partners in this campaign? Partners help me tailor messaging to your ecosystem.",
    type: 'info',
    dismissible: true,
    autoDismissMs: 8000,
  }),
  
  STEP_2_PARTNERS_LOADED: (ctx) => ({
    message: `Perfect! ${ctx?.partnerCount || 'Partners'} added. Ready to build your knowledge base?`,
    type: 'success',
    dismissible: true,
    autoDismissMs: 5000,
  }),
  
  STEP_3_KNOWLEDGE_EMPTY: () => ({
    message: "Let's build your knowledge base. Upload documents, paste URLs, or use Tell Me to add content.",
    type: 'info',
    dismissible: true,
    autoDismissMs: 8000,
  }),
  
  STEP_3_COVERAGE_LOW: () => ({
    message: "Your knowledge base is incomplete. Add more content to improve strategy quality. Aim for at least 40% coverage.",
    type: 'warning',
    dismissible: true,
    autoDismissMs: 10000,
  }),
  
  STEP_3_COVERAGE_MID: () => ({
    message: "Good progress! A bit more content would help, but we can proceed with what you have.",
    type: 'info',
    dismissible: true,
    autoDismissMs: 6000,
  }),
  
  STEP_3_COVERAGE_HIGH: () => ({
    message: "Excellent! Your knowledge base is ready. Let's generate your campaign strategy.",
    type: 'success',
    dismissible: true,
    autoDismissMs: 5000,
  }),
  
  STEP_4_SETUP_REVIEW: () => ({
    message: "Review your setup before generation. Click 'Generate Campaign' when ready.",
    type: 'info',
    dismissible: true,
    autoDismissMs: 8000,
  }),
  
  STEP_5_GENERATION_STARTING: () => ({
    message: "Creating your campaign content. This typically takes 2-3 minutes. Watch the progress theater below!",
    type: 'info',
    dismissible: false,
    autoDismissMs: 10000,
  }),
  
  STEP_5_GENERATION_IN_PROGRESS: (ctx) => ({
    message: `Generating assets... ${ctx?.progress || ''}% complete. Hang tight!`,
    type: 'info',
    dismissible: false,
  }),
  
  STEP_5_GENERATION_COMPLETE: () => ({
    message: "🎉 Campaign content generated! Review each asset and keep, swap, or discard.",
    type: 'success',
    dismissible: true,
    autoDismissMs: 8000,
  }),
  
  STEP_6_REVIEW_ASSETS: () => ({
    message: "Here's what I created. Keep, swap, or discard each asset. You're in control.",
    type: 'info',
    dismissible: true,
    autoDismissMs: 8000,
  }),
  
  STEP_6_ASSETS_APPROVED: (ctx) => ({
    message: `${ctx?.approvedCount || 'Assets'} approved! You can proceed to scheduling or continue refining.`,
    type: 'success',
    dismissible: true,
    autoDismissMs: 6000,
  }),
  
  STEP_12_EXPORT: () => ({
    message: "Great! Your campaign is ready. Download in PDF, HTML, or text format.",
    type: 'success',
    dismissible: true,
    autoDismissMs: 8000,
  }),
};

export const useEmmaState = () => {
  const [emmaState, setEmmaState] = useState<EmmaStateContext>({
    state: 'STEP_1_CLIENT_EMPTY',
    context: {},
    timestamp: new Date(),
  });

  const [currentMessage, setCurrentMessage] = useState<EmmaMessage | null>(null);
  const [messageVisible, setMessageVisible] = useState(false);

  // Update Emma state and trigger message
  const updateEmmaState = useCallback((newState: Partial<EmmaStateContext>) => {
    setEmmaState((prev) => ({
      ...prev,
      ...newState,
      timestamp: new Date(),
    }));
    
    // Generate and show contextual message
    if (newState.state) {
      const messageGenerator = STATE_MESSAGES[newState.state];
      if (messageGenerator) {
        const message = messageGenerator(newState.context);
        setCurrentMessage(message);
        setMessageVisible(true);
        
        // Auto-dismiss if specified
        if (message.autoDismissMs) {
          setTimeout(() => {
            setMessageVisible(false);
          }, message.autoDismissMs);
        }
      }
    }
  }, []);

  // Get current contextual message
  const getContextualMessage = useCallback((): EmmaMessage | null => {
    if (!messageVisible) return null;
    return currentMessage;
  }, [currentMessage, messageVisible]);

  // Dismiss current message
  const dismissMessage = useCallback(() => {
    setMessageVisible(false);
  }, []);

  // Get message for specific state (without updating state)
  const getMessageForState = useCallback((state: EmmaState, context?: Record<string, any>): EmmaMessage => {
    const messageGenerator = STATE_MESSAGES[state];
    return messageGenerator(context);
  }, []);

  // Show a contextual hint (used for workflow step guidance)
  const showHint = useCallback((message: string, autoDismissMs: number = 5000) => {
    setCurrentMessage({
      message,
      type: 'info',
      dismissible: true,
      autoDismissMs,
    });
    setMessageVisible(true);
    
    if (autoDismissMs > 0) {
      setTimeout(() => {
        setMessageVisible(false);
      }, autoDismissMs);
    }
  }, []);

  // Celebrate a milestone (shows success message)
  const celebrate = useCallback((message: string = 'Great job!') => {
    setCurrentMessage({
      message,
      type: 'success',
      dismissible: true,
      autoDismissMs: 4000,
    });
    setMessageVisible(true);
    
    setTimeout(() => {
      setMessageVisible(false);
    }, 4000);
  }, []);

  return {
    emmaState,
    updateEmmaState,
    getContextualMessage,
    dismissMessage,
    getMessageForState,
    messageVisible,
    showHint,
    celebrate,
  };
};

export default useEmmaState;
